import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSavings,useBankList, useMyBanks } from "../../hooks/useSavings";
import { useCurrentMember, useMemberNoK, useMemberAddress } from "../../hooks/useMembers";
import Card from "../../components/ui/Card";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import Avatar from "../../components/ui/Avatar";
import StatCard from "../../components/ui/StatCard";
import Select from "../../components/ui/Select";
import Input from "../../components/ui/Input";
import api from "../../api/client";
import { useQueryClient } from "@tanstack/react-query";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", currencyDisplay: "symbol", maximumFractionDigits: 0 }).format(n);

export default function ProfilePage() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const nokDialogRef = useRef<HTMLDialogElement>(null);
  const kycDialogRef = useRef<HTMLDialogElement>(null);
  const addressDialogRef = useRef<HTMLDialogElement>(null);
  const { user, logout, member, isMemberLoading,memberError }   = useAuth();
  const navigate           = useNavigate();
  const { data: bankdata =[], isLoading} = useBankList() as any;
  const { data }           = useSavings() as any;
  const { data: mybanks = [], isFetching } = useMyBanks() as any;
  const { data: myDetails = [], isSpooling } = useCurrentMember() as any;
  const { data: myNoks = [], isPulling } = useMemberNoK() as any;
  const { data: myAddress = [], isGetting } = useMemberAddress() as any;
  const [formError, setFormError] = useState('');
  const [nokFormError, setNokError] = useState('');
  
  const {account={}, contributions={}, schedule={}} = data || {};

  const charges   = Number(account?.[0]?.charges ?? 0);
  //const balance = Number(account?.[0]?.balance ?? 0) - charges;
  const locked  = Number(account?.[0]?.locked_amount ?? 0);

  const account_savings = account?.[0]?.balance || 0;
  const account_investment = account?.[1]?.balance || 0;
  const account_targeted = account?.[2]?.balance || 0;
  const portfolio = Number(account_savings) + Number(account_investment) + Number(account_targeted) - charges;

  const [form, setForm] = useState({bank:"", accountName:"", accountNo:"", accountType:"",bvn:""});
  const set = (k: string) => (v: string) => setForm(f => ({ ...f, [k]:v }));

  const openForm = () => {
    setForm({bank:mybanks?.[0]?.bank ?? "", accountName:mybanks?.[0]?.account_name ?? "", accountNo:mybanks?.[0]?.account_no ?? "", accountType:mybanks?.[0]?.account_type ?? "",bvn:mybanks?.[0]?.bvn ?? ""});
    dialogRef.current?.showModal() 
  };
  const closeForm = () => { dialogRef.current?.close() };
  const closeBtnStyle = { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' };
  const dialogStyle = {padding: '20px', borderRadius: '12px', border: '1px solid #241917', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '400px', top: '50px', left: '30px', };

  const [nokForm, setNokForm] = useState({fullName:"",dob:"", relationship:"", gender:"",address:"",phone:"", email:""});
  const nset = (l: string) => (w: string) => setNokForm(g => ({...g,[l]:w })); 

  const openNokForm = () => {
    setNokForm({fullName:myNoks?.[0]?.full_name ?? "",dob:myNoks?.[0]?.dob ?? "", relationship:myNoks?.[0]?.relationship ?? "", gender:myNoks?.[0]?.gender ?? "",address:myNoks?.[0]?.address ?? "",phone:myNoks?.[0]?.phone ?? "", email:myNoks?.[0]?.email ?? ""})
    nokDialogRef.current?.showModal() 
  };
  const closeNokForm = () => { nokDialogRef.current?.close() };
  const nokDialogStyle = {padding: '20px', borderRadius: '12px', border: '1px solid #241917', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '500px', top: '50px', left: '30px', };

  const [addressForm, setAddressForm] = useState({homeAddress1:"",homeAddress2:"",city:"",state:"",country:"",occupation:"",jobTitle:"",officeAddress1:"",officeAddress2:""});
  const addset = (a: string) => (b:string) => setAddressForm(c => ({...c,[a]:b}));
  const [addressError, setAddressError] = useState('');
  const openAddressForm = () => {
    setAddressForm({homeAddress1:myAddress?.[0]?.house_address1 ?? "",homeAddress2:myAddress?.[0]?.house_address2 ?? "",city:myAddress?.[0]?.city ?? "",state:myAddress?.[0]?.state ?? "",
      country:myAddress?.[0]?.country ?? "",occupation:myAddress?.[0]?.occupation ?? "",jobTitle:myAddress?.[0]?.job_title ?? "",officeAddress1:myAddress?.[0]?.office_address1 ?? "",officeAddress2:myAddress?.[0]?.office_address2 ?? ""});
    addressDialogRef.current?.showModal();
  };
  const closeAddressForm = () => { addressDialogRef.current?.close() };

  const queryClient = useQueryClient();

  const [docType,   setDocType]   = useState("national_id");
  const [uploaded,  setUploaded]  = useState(false);
  const [fileDesc, setFileDescription] = useState("Click to upload your "+ docType);
  const fpldocument = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");

  const openKycForm = () => { kycDialogRef.current?.showModal() };
  const closeKycForm = () => { kycDialogRef.current?.close() };

  const handleSubmit = async () => {
    if (form.bank ==="" || form.accountName ==="" || form.accountNo ===""){ setFormError('Please fill every required field!'); return;}
    const addbank = await api.post("/banks/bank", {
      bank:form.bank, accountName:form.accountName, accountNo:form.accountNo, accountType:form.accountType
    }) as any;
    if (addbank?.bank==="Not found"){alert('Error: '+addbank.error); return;}
    else{setForm({bank:"", accountName:"", accountNo:"", accountType:"",bvn:""}); setFormError('');}
    queryClient.invalidateQueries({ queryKey: ['banks'] });
    closeForm();
  };

  const handleSaveNok = async () => {
    if (nokForm.fullName ==="" || nokForm.dob ==="" || nokForm.gender ==="" || nokForm.relationship==="" || nokForm.address ==="" || nokForm.phone ==="") {
      setNokError('One or more required field is missing a value!');
      alert('Missing field error!');
      return;
    }
    
    const addNok = await api.post("/members/save-nok", {
      fullName:nokForm.fullName, dob:nokForm.dob,address:nokForm.address, gender:nokForm.gender, relationship:nokForm.relationship, phone:nokForm.phone, email:nokForm.email
    }) as any;

    if (addNok.full_name ==="Not found") {alert('An error occured! '+addNok.error); return;}
    else{setNokForm({fullName:"",dob:"", gender:"", relationship:"",address:"",phone:"", email:""});}
    queryClient.invalidateQueries({ queryKey: ['member-nok'] });
    closeNokForm();
  };

  const handleDocTypeChange = (e: any) =>{
    const newValue = e;
    updateDocType(newValue);
    updateFileDescripion(newValue);
  }
  
  const updateDocType = (value: any) =>{
    setDocType(value);
  }

  const updateFileDescripion = (value: any) => {
    setFileDescription("You selected "+ fileName +" for "+ value);
  }

  const handleFileUpload = async (e: any) => {  
    if (e.target.files.length > 0) {
      //setFile(e.target.files[0]);
      setFileName(e.target.files[0].name);
      setFileDescription("You selected "+ e.target.files[0].name +" for "+ docType);

      if(!e.target.files[0]){
        alert('Select a file to continue!');
        return;
      }
      setFileDescription('Uploading file ...');

      const formData = new FormData();
      formData.append('file', e.target.files[0]);
      formData.append('docType',docType);

      try{
        const response = await api.post("/kyc/document", formData) as any;

        setFileDescription(response.message+' | '+response.fileDetails.originalName +' saved as '+response.fileDetails.savedName);
        if (response.message.includes('success')){
          setUploaded(true);
        }
        
      }
      catch(err: any){
        console.log('Error: '+err.message);
        setFileDescription('No file was uploaded.');
      }
      
    }
  }
  const callFileDialog = () => {
    if (fpldocument.current){
      fpldocument.current.click();      
    }
  }

  const handleKYCUpdate = () =>{
    let k=0;
  };

  const handleSaveAddress = async () =>{
    if (addressForm.homeAddress1==="" || addressForm.city==="" || addressForm.state==="" || addressForm.country===""){
      setAddressError('One/more required filed is missing a value!');
      alert('Missing required field value!');
      return;
    }

    const saveAddress = await api.post("/memberinfo/save-address",{memberId:myDetails?.member_id,
      homeAddress1:addressForm.homeAddress1,homeAddress2:addressForm.homeAddress2,city:addressForm.city,state:addressForm.state,country:addressForm.country,
      occupation:addressForm.occupation,jobTitle:addressForm.jobTitle,officeAddress1:addressForm.officeAddress1,officeAddress2:addressForm.officeAddress2
    }) as any;

    if (saveAddress.house_address1 ==="Not found") {alert('An error occured! '+saveAddress.error); return;}
    else{
      setAddressForm({homeAddress1:"",homeAddress2:"",city:"",state:"",country:"",occupation:"",jobTitle:"",officeAddress1:"",officeAddress2:""});
    }
    queryClient.invalidateQueries({ queryKey: ['member-address'] });
    closeAddressForm();
  };

  return (    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
    <div className="max-w-lg">
      <Card className="mb-4">
        <div className="flex gap-4 items-center mb-5">          
          <Avatar initials={`${myDetails?.first_name?.[0]}${myDetails?.last_name?.[0]}`} size={60}/>
          <div>
            <h2 className="text-lg font-medium text-slate-800">{myDetails?.first_name} {myDetails?.last_name}</h2>
            <p className="text-sm text-slate-500 mb-1.5">{user?.email}</p>
            <Badge label={user?.kyc_status === 'verified'? 'KYC Verified ✅' : user?.kyc_status === 'rejected'? 'KYC Rejected 🚫' : 'KYC Pending ⚠️'} color={user?.kyc_status === 'verified'? "emerald" : user?.kyc_status === 'rejected'? "red" : "amber"}/>
            {user?.kyc_status === 'verified'? "" : user?.kyc_status === 'rejected'? 
              <button onClick={openKycForm} className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-red-600 border-red-300">Update KYC</button> :
              <button className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-300">Update KYC</button>
            }
          </div>

          <dialog ref={kycDialogRef} style={nokDialogStyle} >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom:'15px' }}>
              <h3><span className="text-xl">🪪</span> Update KYC Document</h3>
              <button type="button" onClick={closeKycForm} style={closeBtnStyle}>✕</button>
            </div>
            <h2 className="text-base font-medium text-slate-800 mb-4">Upload ID document</h2>
            <Select label="Document type" value={docType} onChange={(e) => handleDocTypeChange(e)} options={[
              { value:"passport",    label:"International Passport" },
              { value:"national_id", label:"National ID Number (NIN)" },
              { value:"drivers",     label:"Driver's Licence" },
             ]}/>
            
            <input ref={fpldocument} type="file" onChange={(e) => handleFileUpload(e)} style={{display: 'none'}} />
            <div onClick={() => callFileDialog()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              uploaded ? "border-emerald-400 bg-emerald-50" : "border-slate-300 bg-slate-50 hover:border-emerald-300"}`}>
                          
            <div className="text-3xl mb-2">{uploaded ? "✅" : "📄"}</div>
              <p className="text-sm" style={{ color: uploaded ? "#059669" : "#94a3b8" }}>
                {uploaded ? "Document uploaded successfully" : fileDesc}
              </p>
            </div>
          </dialog>

        </div>
        {isSpooling? <p>loading details...</p> : (
        [
          { label:"Account type",  value:"Personal Savings" },
          { label:"KYC status",    value:myDetails?.kyc_status ?? "" },
          { label:"",    value:myDetails?.reject_reason ?? "" },
          { label:"Member No.",    value:myDetails?.member_no ?? "" },
          { label:"Account status",value:myDetails?.status ?? "" },
        ].map(row => (
          <div key={row.label} className="flex justify-between py-2.5 border-t border-slate-100">
            <span className="text-sm text-slate-500">{row.label}</span>
            {(row.label==="" && row.value !="")? <em className="text-sm font-medium text-slate-800 capitalize" style={{color:"red"}}>{row.value}</em> : <span className="text-sm font-medium text-slate-800 capitalize">{row.value}</span>}
          </div>
        ))
        ) }
      </Card>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatCard label="Balance"  value={fmt(portfolio)} color="text-emerald-600"/>
        <StatCard label="Locked"   value={fmt(locked)}  color="text-amber-600"/>
      </div>

      <Card className="mb-4">
        <p className="text-sm font-medium text-slate-700 mb-3" style={{color:"green"}}>Linked bank accounts</p>
        {isFetching? <p>No bank found...</p> :(
        mybanks.map((a:any) => (
          <div key={a.id} className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
            <div className="flex gap-2.5 items-center">
              <span className="text-xl">🏦</span>
              <div>
                <p className="text-sm text-slate-700">{a.bank}</p>
                <p className="text-xs text-slate-400">{a.account_type} ····{a.account_no.toString().slice(-4)}</p>
              </div>
            </div>
            <Badge label="Linked" color="emerald"/>
          </div>
        )))}
        <Button onClick={openForm} variant="ghost" size="sm" className="w-full justify-center mt-3">Add / Edit bank account</Button>
      </Card>

      {/* Pop-up Dialog */}
            <dialog ref={dialogRef} style={dialogStyle} >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom:'15px' }}>
                  <h3><span className="text-xl">🏦</span> Add New Bank</h3>
                  <button type="button" onClick={closeForm} style={closeBtnStyle}>✕</button>
                </div>
                <p style={{color:"red"}}>{formError}</p>
                <Card className="mb-4">
                  {isLoading? <p>Getting list of banks...</p> :
                  <Select label="Bank" value={form.bank} onChange={set("bank")} options={[{value:"", label:"Select bank"},...bankdata?.map((op:any) => ({value:op.bank, label:op.bank})) || []]} /> 
                  }
                  <Input label="Account name" value={form.accountName} onChange={set("accountName")} placeholder="Account name" required />
                  <Input label="Account number" type="number" value={form.accountNo} onChange={set("accountNo")} placeholder="080 0000 0000" required />
                  <Select label="Account Type" value={form.accountType} onChange={set("accountType")} options={[{value:"", label:"Select account type"},{value:"savings", label:"savings"},{value:"current", label:"current"}]} />                  
                  <Button variant="primary" size="sm" onClick={handleSubmit}>
                    Save Bank
                  </Button>
                </Card>
            </dialog>

      {user?.role === "admin" && (
        <Button variant="violet" className="w-full justify-center mb-3" onClick={() => navigate("/admin")}>
          🛡️ Go to Admin Console
        </Button>
      )}
      {/* <Button variant="danger" className="w-full justify-center" onClick={() => { logout(); navigate("/login"); }}>
        Sign out
      </Button> */}
    </div>

    <Card>
      <p className="text-sm font-medium text-slate-700 mb-4" style={{color:"green"}}>Address and Occupation</p>
      {isGetting? <p>No address found...</p> : (
            myAddress.map((addy:any) => (
              <div key={addy.id} className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
                <div className="flex gap-2.5 items-center">
                  <span className="text-xl">📍</span>
                  <div>
                    <p className="text-sm text-slate-700">{addy.house_address1}</p>
                    <p className="text-sm text-slate-700">{addy.house_address2}</p>
                    <p className="text-xs text-slate-400">{addy.city} {addy.state} {addy.country}</p>
                    <em className="text-sm text-slate-700">{addy.occupation} - {addy.job_title}</em>
                    <p className="text-sm text-slate-700">{addy.office_address1}</p>
                    <p className="text-xs text-slate-400">{addy.office_address2}</p>
                  </div>
                </div>
              </div>
            ))
            )}
      <Button onClick={openAddressForm} variant="ghost" size="sm" className="w-full justify-center mt-3" style={{marginBottom:"50px"}}>Add / Edit address</Button>
      
      <dialog ref={addressDialogRef} style={nokDialogStyle} >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom:'15px' }}>
          <h3><span className="text-xl">📍</span> Address / Occupation</h3>          
          <button type="button" onClick={closeAddressForm} style={closeBtnStyle}>✕</button>
        </div>
        <em style={{color:"red"}}>{addressError}</em>
        <Input label="Home address 1" value={addressForm.homeAddress1} onChange={addset("homeAddress1")} placeholder="Enter your home address line 1" required />
        <Input label="Home address 2" value={addressForm.homeAddress2} onChange={addset("homeAddress2")} placeholder="Enter your home address line 2" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1 mb-5">
          <Input label="City" value={addressForm.city} onChange={addset("city")} placeholder="Enter city" required />
          <Input label="State" value={addressForm.state} onChange={addset("state")} placeholder="Enter state" required/>
          <Input label="Country" value={addressForm.country} onChange={addset("country")} placeholder="Enter country" required/>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mb-5">
          <Input label="Occupation / Trade" value={addressForm.occupation} onChange={addset("occupation")} placeholder="Enter occupation or trade"/>
          <Input label="Job title" value={addressForm.jobTitle} onChange={addset("jobTitle")} placeholder="Enter job title" />
        </div>               
        <Input label="Office address 1" value={addressForm.officeAddress1} onChange={addset("officeAddress1")} placeholder="Enter office address line 1" />
        <Input label="Office address (city, state, country)" value={addressForm.officeAddress2} onChange={addset("officeAddress2")} placeholder="Enter office address (city, state, country)" />

        <Button variant="primary" size="sm" onClick={handleSaveAddress}> Save Address</Button>
        
      </dialog>


      <p className="text-sm font-medium text-slate-700 mb-4" style={{color:"green"}}>Details of Next of Kin</p>
        {isPulling? <p>No bank found...</p> :(
        myNoks.map((n:any) => (
          <div key={n.id} className="flex gap-2.5 items-center">
            <span className="text-3xl">{n.gender==="male"? "👦🏽" : "👩🏽"}</span>
            <p className="text-sm text-slate-500 mt-2"><b>{n.full_name}</b></p>
            <p className="text-sm text-slate-500 mt-2">{n.relationship}</p>
            <p className="text-sm text-slate-500 mt-2">{n.phone}</p>
            <p className="text-sm text-slate-500 mt-2">{n.address}</p> 
          </div>
        )))}
        {/* <Button size="sm" className="mt-3" onClick={() => navigate("/loans/apply")}>Add NoK</Button> */}
      <Button onClick={openNokForm} variant="ghost" size="sm" className="w-full justify-center mt-3">Add / Edit next of kin</Button>
      
      <dialog ref={nokDialogRef} style={nokDialogStyle} >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom:'15px' }}>
          <h3><span className="text-xl">👨‍👩‍👦</span> Add Next of Kin</h3>
          <button type="button" onClick={closeNokForm} style={closeBtnStyle}>✕</button>
        </div>
        <em style={{color:"red"}}>{nokFormError}</em>
        <div>
          <Input label="Full name (First name and others)" value={nokForm.fullName} onChange={nset("fullName")} placeholder="Enter full name" required />
          <Input label="Date of Birth" type="date" value={nokForm.dob} onChange={nset("dob")} placeholder="12/02/2000"/>
          <Select label="Gender" value={nokForm.gender} onChange={nset("gender")} options={[{value:"",label:"Select gender"},{value:"male", label:"Male"},{value:"female", label:"Female"}]} />
          <Select label="Relationship" value={nokForm.relationship} onChange={nset("relationship")} options={[{value:"",label:"Select relationship"},
            {value:"spouse", label:"Spouse"},{value:"father", label:"Father"},{value:"mother", label:"Mother"},{value:"uncle", label:"Uncle"},
            {value:"aunt", label:"Aunt"},{value:"brother", label:"Brother"},{value:"sister", label:"Sister"},{value:"family friend", label:"Family friend"}]} />
          <Input label="Full Address" value={nokForm.address} onChange={nset("address")} placeholder="Enter complete address" required />
          <Input label="Phone" type="tel" value={nokForm.phone} onChange={nset("phone")} placeholder="080 0000 0000" />
          <Input label="Email" type="email" value={nokForm.email} onChange={nset("email")} placeholder="jordan@example.com" />            
          <Button variant="primary" size="sm" onClick={handleSaveNok}> Save NoK</Button>
        </div>
      </dialog>
    </Card>    

    </div>
  );
}
